import { useState, useEffect, useCallback } from 'react';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import {
  Loader2, Search, Plus, Trash2, Edit2, Check, X, RefreshCw,
  Building2, Zap, GraduationCap, Award, Shield, MessageSquare,
  Briefcase, Folder, Users, ChevronRight, Merge, AlertTriangle,
  ExternalLink, Eye, LayoutGrid, List, Database, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Database configurations
const DB_CONFIG = {
  skills: { label: 'Skills', icon: Zap, color: 'purple', view: 'table' },
  designations: { label: 'Job Titles', icon: Users, color: 'amber', view: 'table' },
  companies: { label: 'Companies', icon: Building2, color: 'blue', view: 'cards' },
  institutions: { label: 'Institutions', icon: GraduationCap, color: 'green', view: 'table' },
  certifications: { label: 'Certifications', icon: Shield, color: 'indigo', view: 'table' },
  awards: { label: 'Awards', icon: Award, color: 'yellow', view: 'table' },
  languages: { label: 'Languages', icon: MessageSquare, color: 'pink', view: 'table' },
  industries: { label: 'Industries', icon: Briefcase, color: 'cyan', view: 'table' },
  degrees: { label: 'Degrees', icon: GraduationCap, color: 'emerald', view: 'table' },
  projects: { label: 'Projects', icon: Folder, color: 'orange', view: 'table' }
};

export default function MasterDatabasePage() {
  const [activeDb, setActiveDb] = useState('skills');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState('table');
  
  // Selected item for detail view
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemUsage, setItemUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  
  // Inline editing
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Add new item
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [adding, setAdding] = useState(false);
  
  // Merge duplicates
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [mergeThreshold, setMergeThreshold] = useState(80);
  const [selectedDuplicateGroup, setSelectedDuplicateGroup] = useState(null);
  const [selectedMergeIds, setSelectedMergeIds] = useState([]);
  const [merging, setMerging] = useState(false);

  // Multi-select for bulk operations
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [allIdsInDb, setAllIdsInDb] = useState([]);
  const [loadingAllIds, setLoadingAllIds] = useState(false);
  const [selectAllMode, setSelectAllMode] = useState(false); // 'page' or 'all'

  // Company merge from master to main
  const [showCompanyMergeModal, setShowCompanyMergeModal] = useState(false);
  const [mergePreview, setMergePreview] = useState(null);
  const [loadingMergePreview, setLoadingMergePreview] = useState(false);
  const [executingMerge, setExecutingMerge] = useState(false);

  const token = localStorage.getItem('ats_token');
  const headers = { 'Authorization': `Bearer ${token}` };
  const pageSize = 50;

  // Load stats for all databases
  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/master/all/stats`, { headers });
      if (res.ok) {
        const data = await res.json();
        setStats(data.databases || {});
      }
    } catch (e) { console.error('Stats load error:', e); }
  }, []);

  // Load items for active database
  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/master/${activeDb}?search=${encodeURIComponent(search)}&skip=${page * pageSize}&limit=${pageSize}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) { 
      console.error('Load error:', e);
      toast.error('Failed to load data');
    }
    setLoading(false);
  }, [activeDb, search, page]);

  // Load item usage when selected
  const loadItemUsage = useCallback(async (itemId) => {
    setLoadingUsage(true);
    try {
      const res = await fetch(`${API_URL}/api/master/${activeDb}/${itemId}/usage`, { headers });
      if (res.ok) {
        const data = await res.json();
        setItemUsage(data.usage);
      }
    } catch (e) { console.error('Usage load error:', e); }
    setLoadingUsage(false);
  }, [activeDb]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadItems();
    setSelectedItem(null);
    setItemUsage(null);
    // Set view mode based on db type
    setViewMode(DB_CONFIG[activeDb]?.view || 'table');
  }, [activeDb, loadItems]);

  useEffect(() => {
    if (selectedItem) {
      loadItemUsage(selectedItem.id);
    }
  }, [selectedItem, loadItemUsage]);

  // Inline edit handlers
  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValue(item.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async (itemId) => {
    if (!editValue.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/master/${activeDb}/${itemId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editValue.trim() })
      });
      if (res.ok) {
        toast.success('Updated successfully');
        setEditingId(null);
        loadItems();
        loadStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Update failed');
      }
    } catch (e) { toast.error('Update failed'); }
    setSaving(false);
  };

  // Add new item
  const addItem = async () => {
    if (!newItemName.trim()) {
      toast.error('Please enter a name');
      return;
    }
    setAdding(true);
    try {
      const res = await fetch(`${API_URL}/api/master/${activeDb}?name=${encodeURIComponent(newItemName.trim())}`, {
        method: 'POST',
        headers
      });
      if (res.ok) {
        toast.success('Added successfully');
        setShowAddModal(false);
        setNewItemName('');
        loadItems();
        loadStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Add failed');
      }
    } catch (e) { toast.error('Add failed'); }
    setAdding(false);
  };

  // Delete item
  const deleteItem = async (itemId, itemName) => {
    if (!confirm(`Delete "${itemName}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/master/${activeDb}/${itemId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Deleted');
        loadItems();
        loadStats();
        if (selectedItem?.id === itemId) {
          setSelectedItem(null);
        }
        // Remove from selection if selected
        setSelectedIds(prev => prev.filter(id => id !== itemId));
      } else {
        toast.error('Delete failed');
      }
    } catch (e) { toast.error('Delete failed'); }
  };

  // Multi-select handlers
  const toggleSelectItem = (itemId) => {
    setSelectAllMode(false);
    setSelectedIds(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === items.length && !selectAllMode) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map(item => item.id));
      setSelectAllMode(false);
    }
  };

  // Select ALL items in the entire database (across all pages)
  const selectAllInDatabase = async () => {
    setLoadingAllIds(true);
    try {
      // Fetch all IDs from the backend
      const res = await fetch(
        `${API_URL}/api/master/${activeDb}/all-ids`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setAllIdsInDb(data.ids || []);
        setSelectedIds(data.ids || []);
        setSelectAllMode(true);
        toast.success(`Selected all ${data.ids?.length || 0} items in ${config?.label}`);
      } else {
        toast.error('Failed to load all items');
      }
    } catch (e) {
      toast.error('Failed to select all items');
    }
    setLoadingAllIds(false);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAllMode(false);
  };

  // Bulk delete - use efficient bulk endpoint
  const bulkDeleteItems = async () => {
    if (selectedIds.length === 0) return;
    
    setBulkDeleting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/master/${activeDb}/bulk-delete`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: selectedIds })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Deleted ${data.deleted} item${data.deleted !== 1 ? 's' : ''}`);
        loadItems();
        loadStats();
        
        // Clear selected item if it was deleted
        if (selectedItem && selectedIds.includes(selectedItem.id)) {
          setSelectedItem(null);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Delete failed');
      }
    } catch (e) {
      toast.error('Bulk delete failed');
    }
    
    setBulkDeleting(false);
    setShowBulkDeleteConfirm(false);
    setSelectedIds([]);
    setSelectAllMode(false);
  };

  // Clear selection when changing database
  useEffect(() => {
    setSelectedIds([]);
    setSelectAllMode(false);
  }, [activeDb]);

  // Find duplicates
  const findDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const res = await fetch(
        `${API_URL}/api/master/${activeDb}/duplicates?threshold=${mergeThreshold / 100}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setDuplicates(data.groups || []);
        if (data.groups.length === 0) {
          toast.info('No duplicates found with current threshold');
        }
      }
    } catch (e) { toast.error('Failed to find duplicates'); }
    setLoadingDuplicates(false);
  };

  // Merge selected duplicates
  const mergeDuplicates = async () => {
    if (!selectedDuplicateGroup || selectedMergeIds.length === 0) {
      toast.error('Select items to merge');
      return;
    }
    setMerging(true);
    try {
      const res = await fetch(`${API_URL}/api/master/${activeDb}/merge`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          primary_id: selectedDuplicateGroup.primary.id,
          duplicate_ids: selectedMergeIds,
          update_references: true
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setSelectedDuplicateGroup(null);
        setSelectedMergeIds([]);
        findDuplicates();
        loadItems();
        loadStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Merge failed');
      }
    } catch (e) { toast.error('Merge failed'); }
    setMerging(false);
  };

  // Company merge functions (merge companies_master into main companies)
  const loadCompanyMergePreview = async () => {
    setLoadingMergePreview(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/merge/preview`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMergePreview(data);
      } else {
        toast.error('Failed to load preview');
      }
    } catch (e) { 
      toast.error('Failed to load preview'); 
    }
    setLoadingMergePreview(false);
  };

  const executeCompanyMerge = async () => {
    setExecutingMerge(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/merge/execute`, {
        method: 'POST',
        headers
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowCompanyMergeModal(false);
        setMergePreview(null);
        loadItems();
        loadStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Merge failed');
      }
    } catch (e) { 
      toast.error('Merge failed'); 
    }
    setExecutingMerge(false);
  };

  const config = DB_CONFIG[activeDb];
  const IconComponent = config?.icon || Database;

  return (
    <div className="flex flex-col h-full">
      <Header title="Master Database" subtitle="Manage extracted data from CVs" />

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Database List */}
        <div className="w-64 border-r border-slate-200 bg-slate-50 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Databases</h3>
            <div className="space-y-1">
              {Object.entries(DB_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const count = stats[key]?.total || 0;
                const isActive = activeDb === key;
                return (
                  <button
                    key={key}
                    onClick={() => { setActiveDb(key); setPage(0); setSearch(''); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                      isActive 
                        ? `bg-${cfg.color}-100 text-${cfg.color}-700 border border-${cfg.color}-200` 
                        : 'hover:bg-slate-100 text-slate-700'
                    }`}
                    data-testid={`db-${key}`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? `text-${cfg.color}-600` : 'text-slate-400'}`} />
                    <span className="flex-1 font-medium text-sm">{cfg.label}</span>
                    <Badge variant="secondary" className="text-xs">{count}</Badge>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <IconComponent className={`w-5 h-5 text-${config?.color}-600`} />
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{config?.label}</h2>
                <Badge variant="outline">{total} items</Badge>
              </div>
              
              {/* Show bulk actions when items are selected */}
              {selectedIds.length > 0 ? (
                <div className="flex-1 flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                    <Checkbox
                      checked={selectedIds.length === items.length || selectAllMode}
                      onCheckedChange={toggleSelectAll}
                    />
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      {selectedIds.length} selected
                      {selectAllMode && <span className="text-xs ml-1">(all in database)</span>}
                    </span>
                  </div>
                  
                  {/* Select All in Database button - only show if not already in selectAllMode */}
                  {!selectAllMode && selectedIds.length < total && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllInDatabase}
                      disabled={loadingAllIds}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    >
                      {loadingAllIds ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Database className="w-4 h-4 mr-1" />
                      )}
                      Select All {total} in Database
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearSelection}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                  
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {selectedIds.length}
                  </Button>
                </div>
              ) : (
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder={`Search ${config?.label?.toLowerCase()}...`}
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                {/* View toggle */}
                <div className="flex border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`p-2 ${viewMode === 'table' ? 'bg-slate-100 dark:bg-slate-700' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                    title="Table View"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`p-2 ${viewMode === 'cards' ? 'bg-slate-100 dark:bg-slate-700' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                    title="Card View"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowMergeModal(true); findDuplicates(); }}
                  data-testid="find-duplicates-btn"
                >
                  <Merge className="w-4 h-4 mr-2" />
                  Find Duplicates
                </Button>

                {/* Show Sync to Main Companies button only for companies database */}
                {activeDb === 'companies' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setShowCompanyMergeModal(true); loadCompanyMergePreview(); }}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                    data-testid="sync-to-main-btn"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Sync to Main Companies
                  </Button>
                )}

                <Button
                  size="sm"
                  onClick={() => setShowAddModal(true)}
                  data-testid="add-item-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { loadItems(); loadStats(); }}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Content Area - Split View */}
          <div className="flex-1 flex overflow-hidden">
            {/* Items List */}
            <div className={`${selectedItem ? 'w-2/3' : 'w-full'} overflow-auto p-4`}>
              {loading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-20 text-slate-500">
                  <Database className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No items found</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowAddModal(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Add First Item
                  </Button>
                </div>
              ) : viewMode === 'table' ? (
                /* Table View */
                <div className="bg-white dark:bg-slate-900 rounded-lg border dark:border-slate-700 shadow-sm overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700">
                      <tr>
                        <th className="w-12 px-4 py-3">
                          <Checkbox
                            checked={items.length > 0 && selectedIds.length === items.length}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Name</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase w-40">Created</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase w-32">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-slate-700">
                      {items.map((item) => (
                        <tr
                          key={item.id}
                          className={`hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer ${
                            selectedItem?.id === item.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                          } ${selectedIds.includes(item.id) ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}`}
                          onClick={() => setSelectedItem(item)}
                          data-testid={`item-row-${item.id}`}
                        >
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.includes(item.id)}
                              onCheckedChange={() => toggleSelectItem(item.id)}
                              data-testid={`select-item-${item.id}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            {editingId === item.id ? (
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <Input
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="h-8"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveEdit(item.id);
                                    if (e.key === 'Escape') cancelEdit();
                                  }}
                                />
                                <Button size="sm" variant="ghost" onClick={() => saveEdit(item.id)} disabled={saving}>
                                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 text-green-600" />}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={cancelEdit}>
                                  <X className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800 dark:text-slate-200">{item.name}</span>
                                {item.company_code && (
                                  <Badge variant="outline" className="text-xs">{item.company_code}</Badge>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => startEdit(item)}
                                disabled={editingId === item.id}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteItem(item.id, item.name)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Card View */
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {items.map((item) => (
                    <Card
                      key={item.id}
                      className={`cursor-pointer hover:shadow-md transition-shadow ${
                        selectedItem?.id === item.id ? 'ring-2 ring-blue-500' : ''
                      } ${selectedIds.includes(item.id) ? 'ring-2 ring-blue-300 bg-blue-50/50 dark:bg-blue-900/20' : ''}`}
                      onClick={() => setSelectedItem(item)}
                      data-testid={`item-card-${item.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Checkbox for selection */}
                            <div className="flex items-center gap-3 mb-3" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedIds.includes(item.id)}
                                onCheckedChange={() => toggleSelectItem(item.id)}
                                data-testid={`select-card-${item.id}`}
                              />
                              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                <IconComponent className={`w-5 h-5 text-${config?.color}-600`} />
                              </div>
                            </div>
                            {editingId === item.id ? (
                              <div onClick={(e) => e.stopPropagation()}>
                                <Input
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="h-8 mb-2"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveEdit(item.id);
                                    if (e.key === 'Escape') cancelEdit();
                                  }}
                                />
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" onClick={() => saveEdit(item.id)} disabled={saving}>
                                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={cancelEdit}>
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <h3 className="font-semibold text-slate-800 dark:text-slate-200 line-clamp-2">{item.name}</h3>
                                {item.company_code && (
                                  <Badge variant="outline" className="mt-2 text-xs">{item.company_code}</Badge>
                                )}
                              </>
                            )}
                          </div>
                          <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="ghost" onClick={() => startEdit(item)} className="h-7 w-7 p-0">
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteItem(item.id, item.name)}
                              className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 mt-3">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {total > pageSize && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-slate-500">
                    Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of {total}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(page + 1) * pageSize >= total}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Detail Panel */}
            {selectedItem && (
              <div className="w-1/3 border-l border-slate-200 bg-slate-50 overflow-auto">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-800">Details</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedItem(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  <Card>
                    <CardContent className="p-4 space-y-4">
                      <div>
                        <label className="text-xs font-medium text-slate-500 uppercase">Name</label>
                        <p className="font-semibold text-slate-800 mt-1">{selectedItem.name}</p>
                      </div>

                      {selectedItem.company_code && (
                        <div>
                          <label className="text-xs font-medium text-slate-500 uppercase">Code</label>
                          <p className="font-mono text-slate-800 mt-1">{selectedItem.company_code}</p>
                        </div>
                      )}

                      {selectedItem.category && (
                        <div>
                          <label className="text-xs font-medium text-slate-500 uppercase">Category</label>
                          <p className="text-slate-800 mt-1">{selectedItem.category}</p>
                        </div>
                      )}

                      {selectedItem.issuing_org && (
                        <div>
                          <label className="text-xs font-medium text-slate-500 uppercase">Issuing Organization</label>
                          <p className="text-slate-800 mt-1">{selectedItem.issuing_org}</p>
                        </div>
                      )}

                      <div>
                        <label className="text-xs font-medium text-slate-500 uppercase">Created</label>
                        <p className="text-slate-800 mt-1">
                          {selectedItem.created_at ? new Date(selectedItem.created_at).toLocaleString() : 'Unknown'}
                        </p>
                      </div>

                      {selectedItem.updated_at && (
                        <div>
                          <label className="text-xs font-medium text-slate-500 uppercase">Last Updated</label>
                          <p className="text-slate-800 mt-1">{new Date(selectedItem.updated_at).toLocaleString()}</p>
                          {selectedItem.updated_by && (
                            <p className="text-xs text-slate-500">by {selectedItem.updated_by}</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Usage Statistics */}
                  <Card className="mt-4">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Usage Statistics</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loadingUsage ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                        </div>
                      ) : itemUsage ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Candidates</span>
                            <Badge variant={itemUsage.candidates > 0 ? 'default' : 'secondary'}>
                              {itemUsage.candidates}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Jobs</span>
                            <Badge variant={itemUsage.jobs > 0 ? 'default' : 'secondary'}>
                              {itemUsage.jobs}
                            </Badge>
                          </div>
                          {itemUsage.candidates === 0 && itemUsage.jobs === 0 && (
                            <p className="text-xs text-amber-600 flex items-center gap-1 mt-2">
                              <AlertTriangle className="w-3 h-3" />
                              Not used - safe to delete
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500">No usage data</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add New Item Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New {config?.label?.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder={`Enter ${config?.label?.toLowerCase().slice(0, -1)} name`}
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addItem()}
              data-testid="new-item-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={addItem} disabled={adding}>
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Duplicates Modal */}
      <Dialog open={showMergeModal} onOpenChange={setShowMergeModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5" />
              Find & Merge Duplicates - {config?.label}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4 flex-1 overflow-auto">
            {/* Threshold Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Similarity Threshold</label>
                <span className="text-sm text-slate-500">{mergeThreshold}%</span>
              </div>
              <Slider
                value={[mergeThreshold]}
                onValueChange={(v) => setMergeThreshold(v[0])}
                min={50}
                max={100}
                step={5}
              />
              <p className="text-xs text-slate-500">
                Higher threshold = stricter matching (80% recommended)
              </p>
            </div>

            <Button
              variant="outline"
              onClick={findDuplicates}
              disabled={loadingDuplicates}
              className="w-full"
            >
              {loadingDuplicates ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Scan for Duplicates
            </Button>

            {/* Duplicate Groups */}
            {duplicates.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-slate-800">
                  Found {duplicates.length} potential duplicate groups
                </h4>

                {duplicates.map((group, idx) => (
                  <Card
                    key={idx}
                    className={`cursor-pointer transition-all ${
                      selectedDuplicateGroup === group ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => {
                      setSelectedDuplicateGroup(group);
                      setSelectedMergeIds([]);
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{group.count} similar</Badge>
                          <span className="font-semibold text-green-700">Keep: {group.primary.name}</span>
                        </div>
                        <ChevronRight className={`w-4 h-4 transition-transform ${
                          selectedDuplicateGroup === group ? 'rotate-90' : ''
                        }`} />
                      </div>

                      {selectedDuplicateGroup === group && (
                        <div className="mt-3 space-y-2 border-t pt-3" onClick={(e) => e.stopPropagation()}>
                          <p className="text-sm text-slate-600 mb-2">Select items to merge into "{group.primary.name}":</p>
                          {group.duplicates.map((dup) => (
                            <label
                              key={dup.id}
                              className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
                            >
                              <Checkbox
                                checked={selectedMergeIds.includes(dup.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedMergeIds([...selectedMergeIds, dup.id]);
                                  } else {
                                    setSelectedMergeIds(selectedMergeIds.filter(id => id !== dup.id));
                                  }
                                }}
                              />
                              <span className="flex-1">{dup.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {dup.similarity}% match
                              </Badge>
                            </label>
                          ))}

                          <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedMergeIds(group.duplicates.map(d => d.id))}
                            >
                              Select All
                            </Button>
                            <Button
                              size="sm"
                              onClick={mergeDuplicates}
                              disabled={selectedMergeIds.length === 0 || merging}
                              className="bg-amber-500 hover:bg-amber-600"
                            >
                              {merging ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Merge className="w-4 h-4 mr-2" />
                              )}
                              Merge {selectedMergeIds.length} Selected
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {duplicates.length === 0 && !loadingDuplicates && (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Click "Scan for Duplicates" to find similar entries</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Bulk Delete
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Are you sure you want to delete <strong className="text-red-600">{selectedIds.length}</strong> item{selectedIds.length !== 1 ? 's' : ''} from <strong>{config?.label}</strong>?
            </p>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                This action cannot be undone!
              </p>
            </div>
            {selectedIds.length > 5 && (
              <div className="mt-4 max-h-40 overflow-y-auto border rounded-lg p-2">
                <p className="text-xs text-slate-500 mb-2">Items to be deleted:</p>
                <div className="flex flex-wrap gap-1">
                  {items.filter(item => selectedIds.includes(item.id)).map(item => (
                    <Badge key={item.id} variant="outline" className="text-xs">
                      {item.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDeleteConfirm(false)} disabled={bulkDeleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={bulkDeleteItems} 
              disabled={bulkDeleting}
              data-testid="confirm-bulk-delete-btn"
            >
              {bulkDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete {selectedIds.length} Item{selectedIds.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Merge Modal - Sync companies_master to main companies */}
      <Dialog open={showCompanyMergeModal} onOpenChange={setShowCompanyMergeModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Sync Companies to Main Database
            </DialogTitle>
            <DialogDescription>
              Import companies from Master Data into the main Companies collection. 
              <span className="font-semibold text-amber-600"> HR contacts will be preserved</span> for existing companies.
            </DialogDescription>
          </DialogHeader>
          
          {loadingMergePreview ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : mergePreview ? (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-blue-600">Master Companies</p>
                  <p className="text-2xl font-bold text-blue-700">{mergePreview.summary.total_master_companies}</p>
                </div>
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-sm text-green-600">Will be Added</p>
                  <p className="text-2xl font-bold text-green-700">{mergePreview.summary.will_be_added}</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-600">Already Exist (Preserved)</p>
                  <p className="text-2xl font-bold text-amber-700">{mergePreview.summary.already_exist}</p>
                </div>
              </div>
              
              {/* Note */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">Important:</span> {mergePreview.note}
                </p>
              </div>
              
              {/* Sample of companies to add */}
              {mergePreview.samples.companies_to_add.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">
                    Companies that will be added (sample):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {mergePreview.samples.companies_to_add.slice(0, 10).map((c, i) => (
                      <Badge key={i} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {c.name}
                      </Badge>
                    ))}
                    {mergePreview.samples.companies_to_add.length > 10 && (
                      <Badge variant="secondary">+{mergePreview.samples.companies_to_add.length - 10} more</Badge>
                    )}
                  </div>
                </div>
              )}
              
              {/* Sample of already existing */}
              {mergePreview.samples.existing_matches.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">
                    Companies already in main (will be skipped - HR contacts preserved):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {mergePreview.samples.existing_matches.slice(0, 10).map((m, i) => (
                      <Badge key={i} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {m.main.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              Failed to load preview
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompanyMergeModal(false)} disabled={executingMerge}>
              Cancel
            </Button>
            <Button 
              onClick={executeCompanyMerge}
              disabled={executingMerge || !mergePreview || mergePreview.summary.will_be_added === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="execute-merge-btn"
            >
              {executingMerge ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Syncing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Sync {mergePreview?.summary.will_be_added || 0} Companies
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
